/**
 * Feature flags — read from VITE_FF_* environment variables baked in at build time.
 *
 * To add a flag:
 *   1. Add `readonly VITE_FF_<NAME>: string` to ImportMetaEnv in vite-env.d.ts
 *   2. Add the flag below:  myFeature: flag(import.meta.env.VITE_FF_MY_FEATURE, true)
 *   3. Set the value in .env (local dev), .env.production (production defaults),
 *      and as a pipeline variable in Azure DevOps for the dev-SWA environment.
 *
 * Flag precedence at build time (highest → lowest):
 *   Shell env vars (pipeline variables) > .env.production > .env
 */

/** Parses a VITE_FF_* string env var to a boolean with a fallback default. */
export function flag(
  value: string | undefined,
  defaultValue: boolean
): boolean {
  if (value === undefined || value === "") return defaultValue;
  return value === "true";
}

export const features = {
  // Adds "Select All / Clear" buttons to the unallocated tasks panel in AllocateTasksStep.
  // Intended for internal QA only — enabled in local + dev-SWA, disabled in production.
  qaSelectAll: flag(import.meta.env.VITE_FF_QA_SELECT_ALL, false),

  // Adds a global "Select All Available" button above the plot list in AddStagesStep,
  // selecting all non-completed stages across every plot at once.
  // Enabled in local + dev only; disabled in production.
  globalSelectAll: flag(import.meta.env.VITE_FF_GLOBAL_SELECT_ALL, false),

  // Shows the "Variation" row in the Forecast vs Budget tables (forecasting
  // overview + forecasting summary step). Enabled in local + dev-SWA so it can
  // be reviewed; disabled in production where it is hidden in both tables.
  showVariationRow: flag(import.meta.env.VITE_FF_SHOW_VARIATION_ROW, false),

  // Allows creating/reforecasting a forecast for a past month in
  // ForecastingOverviewPage (normally only the current + future months are
  // forecastable). Past months are view-only in every environment — this flag
  // is `false` in .env and every .env.<mode>; flip one locally only to
  // exercise the unlocked behaviour.
  allowPastMonthForecasting: flag(
    import.meta.env.VITE_FF_ALLOW_PAST_MONTH_FORECASTING,
    false
  ),

  // Offers to copy the previous month's labour, plant, material & subcontractor
  // costs in the carry-forward modal on ForecastingOverviewPage. When off, the
  // modal only offers incomplete tasks and no costs are copied.
  carryForwardCosts: flag(import.meta.env.VITE_FF_CARRY_FORWARD_COSTS, false)
} as const;

export type FeatureFlags = typeof features;

/**
 * Which build mode this bundle was built with: 'local' | 'dev' | 'staging' | 'uat' | 'production'.
 *
 * These are build modes, not environment names — they no longer line up: `dev` is Dogma's
 * internal instance, `staging` builds QA and `uat` builds UAT. Consumed by EnvironmentBadge.
 */
export const deployEnv = import.meta.env.VITE_DEPLOY_ENV ?? "local";
